import AddUserAliasDialog from './add-user-aliases-dialog';
import DeleteUserAliasDialog from './delete-user-alias-dialog';
import { useState } from 'react';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getDomainsQuery,
  getUserQuery,
  UserKeys,
} from '../../../../api/users-api';
import TransferUserAliasDialog from './transfer-user-alias-dialog';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useStore } from '../../../state/zustand-store';
import { useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import CreateButton from '../../../../shared/buttons/create-button';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import AlternateEmail from '@mui/icons-material/AlternateEmail';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';

export default function UserAliases() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles } = useUserSettings();

  const { data: domains } = useQuery(getDomainsQuery());
  const {
    data: user,
    isLoading: userIsLoading,
    isFetching: userIsFetching,
  } = useQuery(getUserQuery(userEmail));

  const queryClient = useQueryClient();
  const { colorMode } = useStore();
  const [openAddAlias, setOpenAddAlias] = useState(false);
  const [openDeleteAlias, setOpenDeleteAlias] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openTransferAlias, setOpenTransferAlias] = useState(false);

  const columns: GridColDef[] = [
    {
      field: 'userEmail',
      headerName: 'User Email',
      editable: false,
      sortable: true,
      filterable: true,
      flex: 1,
    },
    {
      field: 'isEditable',
      headerName: 'Editable',
      editable: false,
      sortable: true,
      filterable: true,
      display: 'flex',
      width: 200,
      renderCell: (params) => {
        return params.value === true ? (
          <Check />
        ) : (
          <Tooltip arrow title="Created by Google">
            <Close />
          </Tooltip>
        );
      },
    },
  ];

  if (!user) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  const rows =
    user.aliases?.map((alias) => ({
      id: alias,
      userEmail: alias,
      isEditable: true,
    })) || [];

  const rows2 =
    user.nonEditableAliases?.map((alias) => ({
      id: alias,
      userEmail: alias,
      isEditable: false,
    })) || [];

  const allAlliases = [...rows, ...rows2];

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        noQuickFilter
        primaryButtons={
          <CreateButton
            onClick={() => setOpenAddAlias(true)}
            disabled={
              !isAbleToSeeUIElement(userRoles, UserRoles.AddAliases.value)
            }
            label="Add Alias"
          />
        }
        secondaryButtons={
          <>
            <SharedIconButton
              variant="delete"
              onClick={() => setOpenDeleteAlias(true)}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(userRoles, UserRoles.RemoveAliases.value)
              }
              tooltipLabel="Delete"
            />
            <SharedIconButton
              variant="transfer"
              onClick={() => setOpenTransferAlias(true)}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.TransferAliases.value
                )
              }
              tooltipLabel="Transfer"
            />
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.User, userEmail],
                });
              }}
              tooltipLabel="Refresh"
            />
          </>
        }
      />
    );
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Aliases', <AlternateEmail />),
        }}
        rows={allAlliases}
        columns={columns}
        isRowSelectable={(params: GridRowParams) =>
          params.row.isEditable === true
        }
        sx={{
          '.disable': {
            bgcolor: (theme) =>
              colorMode === 'light'
                ? theme.palette.grey[200]
                : theme.palette.grey[800],
            color: (theme) =>
              colorMode === 'light'
                ? theme.palette.grey[600]
                : theme.palette.grey[400],
            '&:hover': {
              cursor: 'not-allowed',
            },
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        getRowClassName={(params) => {
          return params.row.isEditable === false ? 'disable' : '';
        }}
        loading={userIsLoading || userIsFetching}
        disableColumnSelector
        checkboxSelection
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
      <AddUserAliasDialog
        open={openAddAlias}
        handleClose={() => setOpenAddAlias(false)}
        user={user}
        domains={domains || []}
        changeLogRoles={changeLogRoles}
      />
      <DeleteUserAliasDialog
        open={openDeleteAlias}
        handleClose={() => setOpenDeleteAlias(false)}
        selectedRows={selectedRows}
        userEmail={userEmail}
        user={user}
        changeLogRoles={changeLogRoles}
        setSelectedRows={setSelectedRows}
      />
      <TransferUserAliasDialog
        open={openTransferAlias}
        handleClose={() => setOpenTransferAlias(false)}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        user={user}
        changeLogRoles={changeLogRoles}
      />
    </>
  );
}
